import { useEffect } from 'preact/hooks';
import { useDispatch } from 'react-redux';

import { Button } from '@/components/Buttons';
import PersonaSelector from '@/components/PersonaSelector';
import { useSelector } from '@/redux/helper';
import markAppOnboarded from '@/shared/markAppOnboarded';

import resolveCompletionHeader from '../completionHeader';
import { completionCopy, completionTitle, interpolate } from '../copy';
import { isForOneHabitat } from '../core/completion';
import useChosenProduct from '../hooks/useChosenProduct';
import speedyFlowSlice from '../redux/slice';
import { CompletionVariant } from '../types';

const { advanceSpeedyFlow } = speedyFlowSlice.actions;

interface CompletionStepProps {
  variant: CompletionVariant;
  onDone: () => void;
}

/**
 * The end of every flow.
 *
 * Which variant shows is decided by the machine, from two independent facts: whether money changed
 * hands, and whether we still owe the visitor the persona question. The `*-with-persona` variants
 * fold the selector in here rather than adding a step, because this is the last moment anyone will
 * be asked — there is no skip control, and dismissing is a legitimate answer.
 */
const CompletionStep = ({ variant, onDone }: CompletionStepProps) => {
  const dispatch = useDispatch();

  /**
   * Reaching here is what the retired onboarding chain used to mean: the visitor has an account and
   * has been asked what we wanted to ask. Recorded on arrival rather than on the call to action,
   * because dismissing this card is a legitimate ending too.
   */
  useEffect(() => markAppOnboarded(), []);
  const hasPersona = useSelector((state) => !!state.modals.speedyFlow.machine?.hasPersona);
  const persona = useSelector((state) => state.modals.speedyFlow.machine?.persona);

  // The designed copy names what was bought and where it leads. Both come from data the flow
  // already holds; without them the tokens would print literally, which is what a paying customer
  // used to see.
  const { product, habitat } = useChosenProduct();
  const values = { productName: product?.name, species: habitat?.title };
  const copy = completionCopy[variant];

  // Buying one habitat is confirmed by naming its animals; everything else keeps the generic
  // heading. See `completionTitle` for why both facts have to be in hand.
  const productChoice = useSelector((state) => state.modals.speedyFlow.machine?.productChoice);
  const title = completionTitle(copy, { species: habitat?.title, forOneHabitat: isForOneHabitat(productChoice) });

  /**
   * Figma 8705-8520: the confirmation is headed by a full-bleed banner — but only when the persona
   * question is not folded in. The selector is the tall variant of this card, and a banner above it
   * pushes the button under the fold on a short viewport; the card that only confirms has the room.
   *
   * Which image is a question for `completionHeader`, which expects to grow campaign rules.
   */
  const utm = useSelector((state) => state.modals.speedyFlow.utm);
  const ppt = useSelector((state) => state.modals.speedyFlow.ppt);
  const header = copy.asksPersona ? undefined : resolveCompletionHeader({ utm, ppt, variant });

  return (
    <div className="flex flex-col" data-component="speedy-step" data-step="completion">
      {header && (
        /*
         * Outside the padding on purpose: the banner runs edge to edge, and its own top corners are
         * rounded to the shell's radius so it does not square off the top of the modal if the
         * layer's clipping ever stops doing it for us.
         */
        <img
          src={header.src}
          alt={header.alt}
          aria-hidden={header.alt ? undefined : 'true'}
          className="h-[156px] w-full shrink-0 rounded-t-2xl object-cover"
          data-component="speedy-completion-header"
        />
      )}

      <div className="flex flex-col gap-4 p-6 text-center">
        <h2 className="preflight preflight-h2 text-2xl/7 font-bold text-[#162B1B]">{title}</h2>
        <p className="preflight preflight-p text-sm text-grey-3">{interpolate(copy.body, values)}</p>

        {copy.asksPersona && (
          <PersonaSelector
            value={persona ?? null}
            onChange={(value) => dispatch(advanceSpeedyFlow({ type: 'chosePersona', persona: value }))}
            className="flex flex-col gap-2 text-left"
          />
        )}

        <Button size="lg" disabled={copy.asksPersona && !hasPersona} onClick={onDone}>
          {interpolate(copy.cta, values)}
        </Button>
      </div>
    </div>
  );
};

export default CompletionStep;
